import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { GET_AZURE_RESOURCE_BY_CHECK_ID, GET_AZURE_CHECK_EXCEPTIONS, CREATE_AZURE_CHECK_EXCEPTION, DELETE_AZURE_CHECK_EXCEPTION } from "../../../../apis/organization-apis/Infra-apis/InfraApis";
import InfraResourcesTabBase from "./shared/InfraResourcesTabBase";
import { getDateRangeInUTC, formatDateTime } from "../../../../utils/dateUtils";

const AzureResourcesTab = ({ activeCheckId, onRefetch, onStartPolling, globalException, isTriggerDisabled, checkStatus, remediationEnabled = false, isPollingRunStatus, onRegisterRefetch }) => {
  const [selectedDate, setSelectedDate] = useState(null);

  const { data: azureResourcesData, loading: azureResourcesLoading, refetch: refetchAzureResources } = useQuery(GET_AZURE_RESOURCE_BY_CHECK_ID, {
    variables: {
      check_id: Number(activeCheckId),
      ...(selectedDate ? getDateRangeInUTC(selectedDate) : {}),
    },
    skip: !activeCheckId,
  });

  const { data: azureExceptionsData, refetch: refetchAzureExceptions } = useQuery(GET_AZURE_CHECK_EXCEPTIONS, {
    variables: { filter: { active_check_id: Number(activeCheckId) }, page: 1, limit: 10000 },
    skip: !activeCheckId,
  });

  React.useEffect(() => {
    if (onRegisterRefetch) {
      onRegisterRefetch(async () => {
        await refetchAzureResources();
        await refetchAzureExceptions();
      });
    }
  }, [onRegisterRefetch, refetchAzureResources, refetchAzureExceptions]);

  const [createAzureException, { loading: creatingException }] = useMutation(CREATE_AZURE_CHECK_EXCEPTION);
  const [deleteAzureException] = useMutation(DELETE_AZURE_CHECK_EXCEPTION);

  const mapResource = React.useCallback((resourcesData, exceptions) => {
    if (!resourcesData?.getAzureResourceByCheckId) return [];
    const hasGlobalException = !!globalException;
    return resourcesData.getAzureResourceByCheckId.map((res) => {
      const resourceId = res?.resource_id != null ? String(res.resource_id) : "";
      const accountId = res?.account_id ? String(res.account_id) : null;
      const selectionId = accountId ? `${resourceId}::${accountId}` : resourceId;
      const resourceException = exceptions.find((exc) => {
        const excResourceId = String(exc?.resource_id || "");
        const excAccountId = exc?.account_id ? String(exc.account_id) : null;
        return excResourceId === resourceId && excAccountId === accountId && exc?.is_active === true;
      });
      const hasException = !!resourceException;
      return {
        ...res,
        id: res?.id,
        resourceId,
        selectionId,
        name: res?.resource_name,
        account: res?.account_id,
        status: hasGlobalException || hasException ? "disabled" : (res?.status || "Unknown"),
        reason: hasGlobalException 
          ? "-" 
          : (hasException ? (resourceException?.reason || "-") : (res?.status_reason || "-")),
        exceptionId: resourceException?.id || null,
        exceptionData: resourceException || null,
        exceptionValidUntil: hasException && resourceException?.exception_valid_till ? resourceException.exception_valid_till : null,
      };
    });
  }, [globalException]);

  const azureResources = azureResourcesData?.getAzureResourceByCheckId || [];

  const accountFilterOptions = React.useMemo(() => {
    const accounts = new Set();
    azureResources.forEach((res) => {
      if (res?.account_id) {
        accounts.add(String(res.account_id));
      }
    });
    return Array.from(accounts).map((account) => ({
      id: account,
      label: account,
    }));
  }, [azureResources]);

  const getAccountId = React.useCallback(
    (resource) => resource?.account_id || resource?.subscription_id || resource?.subscription,
    []
  );

  const columns = React.useMemo(() => [
    { label: "Resource Name", field: "name", align: "left", width: "14%" },
    { label: "Resource ID", field: "resourceId", align: "left", width: "14%" },
    {
      label: "Subscription ID",
      field: "account",
      align: "left",
      width: "14%",
      filterConfig: accountFilterOptions.length ? { filterOptions: accountFilterOptions } : undefined,
    },
    { label: "Latest Execution Time", field: "policy_execution_time", align: "left", width: "14%" },
  ], [accountFilterOptions]);

  return (
    <InfraResourcesTabBase
      providerName="Azure"
      activeCheckId={activeCheckId}
      onRefetch={onRefetch}
      onStartPolling={onStartPolling}
      resourcesData={azureResourcesData}
      resourcesLoading={azureResourcesLoading}
      exceptionsData={azureExceptionsData?.getAzureCheckExceptions}
      refetchExceptions={refetchAzureExceptions}
      refetchResources={refetchAzureResources}
      createException={createAzureException}
      creatingException={creatingException}
      deleteException={deleteAzureException}
      mapResource={mapResource}
      columns={columns}
      getAccountId={getAccountId}
      globalException={globalException}
      isTriggerDisabled={isTriggerDisabled}
      checkStatus={checkStatus}
      remediationEnabled={remediationEnabled}
      isPollingRunStatus={isPollingRunStatus}
      selectedDate={selectedDate}
      onDateChange={setSelectedDate}
    />
  );
};

export default AzureResourcesTab;
